import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (isRegister) await register(name, email, password);
      else await login(email, password);
      toast.success(isRegister ? "Welcome to Skinsights" : "Welcome back");
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-50">
      <div className="hidden lg:flex relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1773674489785-0d369e061290?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-sand-50/60 via-transparent to-sage-100/40"/>
        <div className="relative z-10 m-auto max-w-md p-12">
          <p className="text-xs uppercase tracking-[0.2em] text-sage-600">Skinsights</p>
          <h2 className="font-serif text-5xl tracking-tight text-stone-900 mt-4">A daily ritual for your skin.</h2>
          <p className="text-stone-700 mt-6 leading-relaxed">Track, treat, and notice the small changes — together.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <Link to="/" className="font-serif text-2xl text-stone-900" data-testid="auth-brand">Skin<span className="text-sage-400">sights</span></Link>
          <h1 className="font-serif text-4xl mt-8 text-stone-900">{isRegister ? "Create your journal" : "Welcome back"}</h1>
          <p className="text-sm text-stone-500 mt-2">{isRegister ? "Start tracking in under a minute." : "Sign in to continue your tracking."}</p>

          <div className="space-y-4 mt-8">
            {isRegister && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name-input" className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl"/>
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input" className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl"/>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="auth-password-input" className="mt-1.5 bg-stone-50 border-stone-200 rounded-xl"/>
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mt-3" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={busy} className="w-full mt-6 rounded-full bg-sage-400 hover:bg-sage-500 text-white py-6" data-testid="auth-submit-btn">
            {busy ? "..." : isRegister ? "Create account" : "Sign in"}
          </Button>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-widest">
              <span className="bg-sand-50 px-3 text-stone-400">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={busy}
            data-testid="auth-google-btn"
            className="w-full rounded-full border border-stone-200 bg-white hover:bg-stone-50 text-stone-800 py-3 flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <p className="text-sm text-stone-500 mt-6 text-center">
            {isRegister ? <>Already have an account? <Link to="/login" className="text-sage-500 hover:underline" data-testid="auth-switch-login">Sign in</Link></>
                       : <>New here? <Link to="/register" className="text-sage-500 hover:underline" data-testid="auth-switch-register">Create an account</Link></>}
          </p>

          {!isRegister && (
            <div className="mt-6 pt-6 border-t border-stone-200 text-[11px] text-stone-400 text-center">
              By signing in you agree to our <Link to="/terms" className="underline hover:text-stone-600">Terms</Link> and <Link to="/privacy" className="underline hover:text-stone-600">Privacy Policy</Link>.<br/>
              Skinsights is a personal wellness journal — not medical advice.
            </div>
          )}
          {isRegister && (
            <div className="mt-4 text-[11px] text-stone-400 text-center">
              By creating an account you agree to our <Link to="/terms" className="underline hover:text-stone-600">Terms</Link> and <Link to="/privacy" className="underline hover:text-stone-600">Privacy Policy</Link>.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
